import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../ui/dialog';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { Label } from '../../ui/label';
import { Textarea } from '../../ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../ui/select';
import { Loader2, Save } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const REASONS = ['Used', 'Damaged / Lost', 'Inventory Correction'];

const AdjustStockDialog = ({ product, onOpenChange, headers, onSaved }) => {
  const [direction, setDirection] = useState('decrease');
  const [qty, setQty] = useState('');
  const [reason, setReason] = useState('Used');
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => { if (product) { setDirection('decrease'); setQty(''); setReason('Used'); setNote(''); } }, [product]);

  if (!product) return null;

  const save = async () => {
    if (!qty || parseFloat(qty) <= 0) { toast.error('Quantity must be positive'); return; }
    setBusy(true);
    try {
      const delta = direction === 'increase' ? parseFloat(qty) : -parseFloat(qty);
      await axios.post(`${API_URL}/api/admin/inventory/products/${product.id}/adjust`,
        { quantity_delta: delta, reason, note: note || undefined }, { headers });
      toast.success('Stock adjusted');
      onSaved(); onOpenChange(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to adjust');
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={!!product} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-sm" data-testid="adjust-dialog">
        <DialogHeader><DialogTitle>Adjust {product.name}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <p className="text-xs text-zinc-500">Current stock: <b>{product.quantity_on_hand}</b></p>
          <div className="grid grid-cols-2 gap-2">
            <button onClick={() => setDirection('increase')}
              className={`py-2 rounded-lg text-xs font-medium border ${direction === 'increase' ? 'bg-emerald-50 border-emerald-300 text-emerald-700' : 'border-zinc-200'}`}
              data-testid="adjust-inc">+ Increase</button>
            <button onClick={() => setDirection('decrease')}
              className={`py-2 rounded-lg text-xs font-medium border ${direction === 'decrease' ? 'bg-red-50 border-red-300 text-red-700' : 'border-zinc-200'}`}
              data-testid="adjust-dec">− Decrease</button>
          </div>
          <F label="Quantity *"><Input type="number" step="1" value={qty} onChange={e => setQty(e.target.value)} data-testid="adjust-qty" /></F>
          <F label="Reason *">
            <Select value={reason} onValueChange={setReason}>
              <SelectTrigger data-testid="adjust-reason"><SelectValue /></SelectTrigger>
              <SelectContent>{REASONS.map(r => <SelectItem key={r} value={r}>{r}</SelectItem>)}</SelectContent>
            </Select>
          </F>
          <F label="Note (optional)"><Textarea rows={2} value={note} onChange={e => setNote(e.target.value)} /></F>
          <div className="flex justify-end gap-2 pt-2 border-t">
            <Button variant="outline" onClick={() => onOpenChange(false)} disabled={busy}>Cancel</Button>
            <Button onClick={save} disabled={busy} className="bg-[#F56F05] hover:bg-[#e06505] text-white" data-testid="adjust-save">
              {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
              Save
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
const F = ({ label, children }) => (<div className="space-y-1"><Label className="text-xs text-zinc-600">{label}</Label>{children}</div>);

export default AdjustStockDialog;
